import { zodResolver } from '@hookform/resolvers/zod';
import DoneIcon from '@mui/icons-material/Done';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { checkPhoneVerificationCode, sendPhoneVerificationCode } from 'api';
import { updateTeamSettings } from 'api/settings';
import { Button } from 'components/Ui-V3/Button';
import { useCountDownTimer } from 'hooks/useCountDownTimer';
import { TEAM_ADMIN, useUserContext } from 'stores/user';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getColor } from 'theme';
import { EColors } from 'types/themenum';
import * as z from 'zod';
import { Description, Divider, ProfileFormData, Title } from './ProfileDataSave';

const INITIAL_TIME = 120; // 2 minutes in seconds

const verificationSchema = z.object({
    code: z.array(z.string().regex(/^\d$/)).length(6),
});

type VerificationFormData = z.infer<typeof verificationSchema>;

interface VerificationCodeInputProps {
    formData: ProfileFormData | null;
    onSuccess: () => void;
    onGoBack: () => void;
}

// Simple regex-based phone number formatter
const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';

    // Extract only digits
    const digitsOnly = phone.replace(/\D/g, '');

    // Format 10-digit numbers as (XXX) XXX-XXXX
    if (digitsOnly.length === 10) {
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }

    // Handle 11-digit numbers starting with 1 (US country code)
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        const withoutCountry = digitsOnly.slice(1);
        return `(${withoutCountry.slice(0, 3)}) ${withoutCountry.slice(3, 6)}-${withoutCountry.slice(6)}`;
    }

    // Return original for other cases
    return phone;
};

const StyledTextField = styled(TextField, {
    shouldForwardProp: prop => prop !== 'error',
})<{ error?: boolean }>(({ error }) => ({
    '& .MuiInputBase-root': {
        height: '75px',
        width: '75px',
        '&.Mui-focused.Mui-focused > .MuiOutlinedInput-notchedOutline': {
            border: `1.588px solid ${error ? getColor(EColors.v3Red) : getColor(EColors.v3Dark)} !important`,
        },
        backgroundColor: getColor(EColors.v3Background),
        borderRadius: '19px',
        '& input:not([value=""]) ~ .MuiOutlinedInput-notchedOutline': {
            border: `1.588px solid ${error ? getColor(EColors.v3Red) : getColor(EColors.v3Dark)} !important`,
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: error ? `1.588px solid ${getColor(EColors.v3Red)}` : `1px solid ${getColor(EColors.bizlyOSGray20)}`,
        borderRadius: '19px',
    },
    '& input': {
        color: error ? getColor(EColors.warningText) : 'inherit',
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: '1.125em',
        fontFamily: '"General Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
    },
}));

const OtpDescription = styled(Typography, {
    shouldForwardProp: prop => prop !== 'error',
})<{ error?: boolean }>(({ error }) => ({
    fontSize: '.875rem',
    fontWeight: 500,
    lineHeight: '1.125em',
    color: getColor(EColors.v3TextSecondary),
    fontFamily: '"General Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
    marginTop: error ? '17px' : '44px',
    marginBottom: '8px',
    '& span': {
        fontWeight: 600,
        color: getColor(EColors.v3TextPrimary),
    },
}));

const OtpDescription2 = styled(OtpDescription)({
    margin: '0px',
    '& span': {
        textDecoration: 'underline',
        cursor: 'pointer',
        color: getColor(EColors.v3TextPrimary),
        fontWeight: 500,
    },
});

const SuccessIconWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '81px',
    width: '81px',
    borderRadius: '50%',
    backgroundColor: getColor(EColors.v3Success),
});

const ErrorMessage = styled(Typography)({
    color: getColor(EColors.v3Red),
    fontWeight: 500,
    lineHeight: '19px',
    fontFamily: '"General Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
    marginTop: '8px',
});

export const VerifyPhone = ({ formData, onSuccess, onGoBack }: VerificationCodeInputProps) => {
    const { user, updateUser, setTeam } = useUserContext();
    const [error, setError] = useState<string | null>(null);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [verificationSuccess, setVerificationSuccess] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const { timeLeft, resetTimer, formatTime, cancelTimer } = useCountDownTimer(INITIAL_TIME);
    const { control, setValue, getValues } = useForm<VerificationFormData>({
        defaultValues: {
            code: ['', '', '', '', '', ''],
        },
        resolver: zodResolver(verificationSchema),
    });

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const verificationAttempted = useRef(false);

    // Handle timer expiration - go back to step 1
    useEffect(() => {
        if (timeLeft === 0) {
            onGoBack();
        }
    }, [timeLeft, onGoBack]);

    const handleResendCode = async () => {
        if (user?.phone) await sendPhoneVerificationCode({ phone: user?.phone });
        setValue('code', ['', '', '', '', '', '']);
        setError(null);
        resetTimer();
    };

    const handleVerifyCode = useCallback(async () => {
        try {
            if (!formData?.phone) {
                throw new Error('Phone is required');
            }

            setIsVerifying(true);

            if (verificationAttempted.current) return;
            verificationAttempted.current = true;

            const code = getValues('code').join('');
            const { success } = await checkPhoneVerificationCode({ code });

            if (success) {
                // Update user profile after successful verification
                await updateUser({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                });

                // Update team settings if team exists
                if (user.team?.id && user.team?.role === TEAM_ADMIN) {
                    const { team } = await updateTeamSettings(user.team?.id, {
                        name: formData.companyName,
                        companySize: formData.companySize,
                    });

                    setTeam(team);
                }

                cancelTimer();
                setFailedAttempts(0);
                setVerificationSuccess(true);
                setTimeout(() => {
                    onSuccess();
                }, 3000);
            } else {
                throw new Error('Invalid verification code');
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: 'VerifyPhone',
                    action: 'phone_verification',
                    failedAttempts: failedAttempts.toString(),
                },
                extra: {
                    phone: formData?.phone,
                    userId: user?.id,
                },
            });
            const message = error instanceof Error ? error.message : 'Invalid verification code';
            setError(message);
            setFailedAttempts(prev => prev + 1);
        } finally {
            verificationAttempted.current = false;
            setIsVerifying(false);
        }
    }, [user?.phone, user?.team?.id, formData, updateUser, setTeam, getValues, cancelTimer, onSuccess]);

    useEffect(() => {
        if (failedAttempts === 3) {
            const timer = setTimeout(() => {}, 3000);
            return () => clearTimeout(timer);
        }
    }, [failedAttempts]);

    const handleChange = (index: number, value: string) => {
        const newValue = value.replace(/\D/g, '');
        const currentCode = getValues('code');
        const newCode = [...currentCode];
        newCode[index] = newValue;

        setValue('code', newCode);

        if (newValue && index < 5) {
            inputRefs.current[index + 1]?.focus();
        } else if (newCode.every(c => c.length === 1)) {
            handleVerifyCode();
        }
        setError(null);
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const currentCode = getValues('code');
            const newCode = [...currentCode];
            newCode[index] = '';
            setValue('code', newCode);

            if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
            setError(null);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();

        const numericRegex = /^\d+$/;
        const pastedData = e.clipboardData.getData('text/plain');

        if (numericRegex.test(pastedData)) {
            const numbers = pastedData.slice(0, 6).split('');
            const newCode = [...numbers, ...Array(6 - numbers.length).fill('')];
            setValue('code', newCode, { shouldValidate: true, shouldDirty: true });

            const nextEmptyIndex = Math.min(numbers.length, 5);
            inputRefs.current[nextEmptyIndex]?.focus();
            setError(null);
        }
    };

    if (verificationSuccess) {
        return (
            <Stack justifyContent="center" alignItems="center" height="100%" gap={8} zIndex={1}>
                <SuccessIconWrapper>
                    <DoneIcon fontSize="large" sx={{ color: getColor(EColors.pureWhite), fontSize: 26 }} />
                </SuccessIconWrapper>
                <Title>Phone verified successfully</Title>
            </Stack>
        );
    }

    return (
        <Stack justifyContent="space-between" height="100%">
            <Box>
                <Title>Verify phone number</Title>
                <Description>We have sent the 6-digit code to {formatPhoneNumber(formData?.phone)}.</Description>
                <Divider sx={{ marginTop: '40px', marginBottom: '40px' }} />
                <Box display="flex" gap={0.75}>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <Controller
                            key={index}
                            name={`code.${index}`}
                            control={control}
                            render={({ field }) => (
                                <StyledTextField
                                    {...field}
                                    value={field.value || ''}
                                    inputRef={el => (inputRefs.current[index] = el)}
                                    variant="outlined"
                                    error={Boolean(error)}
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: 'center' },
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                    }}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleChange(index, e.target.value)
                                    }
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                />
                            )}
                        />
                    ))}
                </Box>
                {error && <ErrorMessage>Wrong OTP entered</ErrorMessage>}

                <OtpDescription error={Boolean(error)}>
                    The code will expire in: <span>{formatTime(timeLeft)}</span>
                </OtpDescription>
                <OtpDescription2 variant="body1" onClick={handleResendCode}>
                    Didn't get the code? <span>Resend</span>
                </OtpDescription2>
            </Box>
            <Button onClick={handleVerifyCode} disabled={isVerifying} loading={isVerifying}>
                {isVerifying ? 'Verifying...' : 'Verify code'}
            </Button>
        </Stack>
    );
};
